/**
 * O convite para entrar na competição.
 *
 * No template: texto e lista de marcadores à esquerda, imagem grande à direita,
 * e uma forma recortada (`.community-union`) encaixando as duas metades. É a
 * seção mais editorial da página — não tem dado nenhum, só argumento.
 *
 * A metade da direita, que no template era uma foto, é o convite para o grupo
 * oficial da competição no WhatsApp — com destaque de propósito: é por lá que
 * a comunidade conversa, recebe os avisos e fica sabendo das edições novas.
 * Uma foto de banco de imagens ocuparia o mesmo lugar sem levar ninguém a
 * lugar nenhum.
 */

import { useRef } from 'react';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';
import { COMUNIDADE } from './conteudo';

export function SecaoComunidade() {
  const raiz = useRef<HTMLElement>(null);
  useEntradaDaSecao(raiz, '[data-entra]');

  return (
    <section className="community-section" ref={raiz}>
      <div className="container">
        <div className="community-wrapper">
          <div className="community-contents">
            <div className="community-top-detail">
              <h2 className="community-title" data-entra>
                {COMUNIDADE.titulo}
              </h2>
              <p className="large-paragraph community-para" data-entra>
                {COMUNIDADE.texto}
              </p>

              <div className="community-lists" data-entra>
                {COMUNIDADE.itens.map((item) => (
                  <div key={item} className="community-list">
                    <IconeDeCheck />
                    <div className="community-list-item">{item}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="community-buttons mpc-comunidade-botoes" data-entra>
              <Link to={COMUNIDADE.botao.destino} className="button-3">
                <div className="button-3-text">{COMUNIDADE.botao.rotulo}</div>
                <div className="button-3-bg" aria-hidden />
              </Link>
              <a
                href={COMUNIDADE.whatsapp.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mpc-botao-whats-contorno"
              >
                <IconeWhatsApp />
                Grupo no WhatsApp
              </a>
            </div>
          </div>

          {/* ---------------------------------------- o grupo oficial */}
          <aside className="mpc-whats-card" data-entra aria-label="Grupo oficial no WhatsApp">
            <span aria-hidden className="mpc-whats-marca">
              <IconeWhatsApp />
            </span>
            <p className="mpc-whats-selo">
              <span className="mpc-whats-ponto" aria-hidden />
              Oficial da competição
            </p>
            <h3 className="mpc-whats-titulo">{COMUNIDADE.whatsapp.titulo}</h3>
            <p className="mpc-whats-texto">{COMUNIDADE.whatsapp.texto}</p>
            <ul className="mpc-whats-itens">
              {COMUNIDADE.whatsapp.itens.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <a
              href={COMUNIDADE.whatsapp.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mpc-whats-botao"
            >
              <IconeWhatsApp />
              {COMUNIDADE.whatsapp.botao}
            </a>
            <p className="mpc-whats-rodape">Abre o WhatsApp · grátis · sai quando quiser</p>
          </aside>
        </div>
      </div>
    </section>
  );
}

/** O ícone do WhatsApp, desenhado aqui para não depender de CDN de terceiro. */
function IconeWhatsApp() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="mpc-icone-whats">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28Z" />
    </svg>
  );
}

/**
 * O marcador da lista.
 *
 * No template é uma imagem PNG de 4 KB baixada de um CDN, repetida quatro
 * vezes. Aqui é um SVG inline de meia dúzia de bytes que herda `currentColor` —
 * e que, por isso, acompanha o tema em vez de ficar com a cor em que foi salvo.
 */
function IconeDeCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="community-check-icon" aria-hidden>
      <circle cx="12" cy="12" r="10" fill="var(--_colors---text-color--bright-red)" />
      <path
        d="m8 12.2 2.7 2.7L16 9.6"
        stroke="var(--_colors---background-color--dark)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
